import { Button } from "@shared/ui/button";
import { FileText, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UploadedDocumentsReviewProps {
  data?: any;
  onEdit?: () => void;
}

export function UploadedDocumentsReview({
  data,
  onEdit,
}: UploadedDocumentsReviewProps) {
  const { t } = useTranslation();
  console.log("📄 Documents Data in Review:", data);

  // Group documents by type for display
  const groupedDocuments = () => {
    if (!data?.documents) return [];

    // Get all uploaded documents (with file or url)
    const uploadedDocs = data.documents.filter(
      (doc: any) => (doc.file || doc.url) && !doc.needsReupload
    );

    // Group by document type (category)
    const grouped = uploadedDocs.reduce((acc: any, doc: any) => {
      const type = doc.category || doc.documentType || "Other";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    }, {});

    return Object.entries(grouped).map(([type, docs]: [string, any]) => ({
      title: type,
      subtype: docs[0]?.subCategory || docs[0]?.documentSubtype || "",
      files: docs,
      count: docs.length,
    }));
  };

  const documentGroups = groupedDocuments();

  // Check if no documents uploaded
  if (!data || !data.documents || documentGroups.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {t("Uploaded Documents")}
          </h2>
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>{t("No documents uploaded yet")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {t("Uploaded Documents")}
        </h2>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>

      <div className="space-y-4">
        {documentGroups.map((group, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Document Type Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-base font-medium text-gray-900">
                      {group.title}
                    </p>
                    {group.subtype && (
                      <p className="text-sm text-gray-600">
                        {t("Sub-type")}: {group.subtype}
                      </p>
                    )}
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {group.count} {group.count > 1 ? t("files") : t("file")} {t("uploaded")}
                </span>
              </div>
            </div>

            {/* Files List */}
            <div className="divide-y divide-gray-200">
              {group.files.map((file: any, fileIndex: number) => (
                <div
                  key={fileIndex}
                  className="px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name || t("Document")}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {file.size > 0 && (
                            <span className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(2)} KB
                            </span>
                          )}
                          {file.subCategory && (
                            <>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {file.subCategory}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {file.url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 flex-shrink-0"
                        onClick={() => window.open(file.url, "_blank")}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Summary Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{t("Total Documents")}</span>
          <span className="font-medium text-gray-900">
            {t("{{valid}} of {{total}} uploaded", { valid: data.validFileCount || 0, total: data.totalFileCount || 0 })}
          </span>
        </div>
      </div>
    </div>
  );
}